/**
 * Animation easing functions.
 *
 * See type declaration AnimationEasingDeclaration for list of predefined easings
 *
 * Additional easing function can be added with addEasing(..). This function will also register it
 * with jQuery, if jQuery is available.
 *
 * Example referencing an easing function, creating a new bezier one, and then using it.
 *
 * import AnimationEasing from './AnimationEasing';
 * console.log( AnimationEasing.func.easeInBack );
 *
 * AnimationEasing.addEasing('interaction', AnimationEasing.bezier(.3,1,.8,1));
 * AnimationEasing.addEasing('noInteraction', AnimationEasing.bezier(.4,.69,.5,1));
 * console.log( AnimationEasing.func.interaction );
 * console.log( AnimationEasing.func.noInteraction );
 *
 * The build in easing functions are based on jQueryUI. Documentation (with nice graphs): http://api.jqueryui.com/easings/
 */
import $ from 'jquery';

export interface EaseFunc {
	(x:number): number;
}

export interface AnimationEasingDeclaration {
	linear: EaseFunc;
	swing: EaseFunc;
	easeInQuad: EaseFunc;
	easeOutQuad: EaseFunc;
	easeInOutQuad: EaseFunc;
	easeInCubic: EaseFunc;
	easeOutCubic: EaseFunc;
	easeInOutCubic: EaseFunc;
	easeInQuart: EaseFunc;
	easeOutQuart: EaseFunc;
	easeInOutQuart: EaseFunc;
	easeInQuint: EaseFunc;
	easeOutQuint: EaseFunc;
	easeInOutQuint: EaseFunc;
	easeInExpo: EaseFunc;
	easeOutExpo: EaseFunc;
	easeInOutExpo: EaseFunc;
	easeInSine: EaseFunc;
	easeOutSine: EaseFunc;
	easeInOutSine: EaseFunc;
	easeInCirc: EaseFunc;
	easeOutCirc: EaseFunc;
	easeInOutCirc: EaseFunc;
	easeInElastic: EaseFunc;
	easeOutElastic: EaseFunc;
	easeInOutElastic: EaseFunc;
	easeInBack: EaseFunc;
	easeOutBack: EaseFunc;
	easeInOutBack: EaseFunc;
	easeInBounce: EaseFunc;
	easeOutBounce: EaseFunc;
	easeInOutBounce: EaseFunc;
	[key: string]: EaseFunc;
}

function addEasing(name: string, fn: EaseFunc): typeof fn {
	// Add to dictionary
	func[name] = fn;

	// Add to jQuery
	(<any>$).easing[name] = fn;
	return fn;
}

/**
 * Function that creates bezier curve
 */
function bezier(mX1:number, mY1:number, mX2:number, mY2:number): EaseFunc {
	if (mX1 == mY1 && mX2 == mY2) {
		return function(aX) { return aX; };
	} else {
		return function(aX) { return CalcBezier(GetTForX(aX), mY1, mY2); };
	}

	function A(aA1:number, aA2:number) {
		return 1.0 - 3.0 * aA2 + 3.0 * aA1;
	}
	function B(aA1:number, aA2:number) {
		return 3.0 * aA2 - 6.0 * aA1;
	}
	function C(aA1:number) {
		return 3.0 * aA1;
	}

	// Returns x(t) given t, x1, and x2, or y(t) given t, y1, and y2.
	function CalcBezier(aT:number, aA1:number, aA2:number) {
		return ((A(aA1, aA2)*aT + B(aA1, aA2))*aT + C(aA1))*aT;
	}

	// Returns dx/dt given t, x1, and x2, or dy/dt given t, y1, and y2.
	function GetSlope(aT:number, aA1:number, aA2:number) {
		return 3.0 * A(aA1, aA2)*aT*aT + 2.0 * B(aA1, aA2) * aT + C(aA1);
	}

	// Newton raphson iteration
	function GetTForX(aX:number): number {
		let aGuessT = aX;
		for (let i = 0; i < 4; ++i) {
			const currentSlope = GetSlope(aGuessT, mX1, mX2);
			if (currentSlope == 0.0)
				return aGuessT;
			const currentX = CalcBezier(aGuessT, mX1, mX2) - aX;
			aGuessT -= currentX / currentSlope;
		}
		return aGuessT;
	}
}

// Create an object that contains all base easing functions
const baseEasings:{[key: string]: EaseFunc} = {
	Sine(p) {
		return 1 - Math.cos(p * Math.PI / 2);
	},
	Circ(p) {
		return 1 - Math.sqrt(1 - p * p);
	},
	Elastic(p) {
		return p === 0 || p === 1 ? p :
			-Math.pow(2, 8 * (p - 1)) * Math.sin(((p - 1) * 80 - 7.5) * Math.PI / 15);
	},
	Back(p) {
		return p * p * (3 * p - 2);
	},
	Bounce(p) {
		let pow2, bounce = 4;
		while (p < ((pow2 = Math.pow(2, --bounce)) - 1) / 11) {}
		return 1 / Math.pow(4, 3 - bounce) - 7.5625 * Math.pow((pow2 * 3 - 2) / 22 - p, 2);
	},

	Quad(p) { return Math.pow(p, 2); },
	Cubic(p) { return Math.pow(p, 3); },
	Quart(p) { return Math.pow(p, 4); },
	Quint(p) { return Math.pow(p, 5); },
	Expo(p) { return Math.pow(p, 6); }
};

const func: AnimationEasingDeclaration = {
	linear(p) {
		return p;
	},
	swing(p) {
		return 0.5 - Math.cos(p * Math.PI) / 2;
	},

	...addVariations('Quad'),
	...addVariations('Cubic'),
	...addVariations('Quart'),
	...addVariations('Quint'),
	...addVariations('Expo'),
	...addVariations('Sine'),
	...addVariations('Circ'),
	...addVariations('Elastic'),
	...addVariations('Back'),
	...addVariations('Bounce')
};

function addVariations<T extends keyof typeof baseEasings>(name: T) {
	const addEasingForInit = (name: string, fn: EaseFunc): typeof fn => $.easing[name] = fn;
	const easeFn = baseEasings[name];
	return {
		[`easeIn${name}`]: addEasingForInit(`easeIn${name}`, easeFn),
		[`easeOut${name}`]: addEasingForInit(`easeOut${name}`, p => 1 - easeFn(1 - p)),
		[`easeInOut${name}`]: addEasingForInit(`easeInOut${name}`,
			p => p < 0.5
				? easeFn(p * 2) / 2
				: 1 - easeFn(p * -2 + 2) / 2)
	} as Record<`ease${`In`|`Out`|`InOut`}${typeof name}`, EaseFunc>;
}

export default {
	func, addEasing, bezier
};
